import qs from 'qs'

import HttpClient from 'services/HttpClient'

/** Manual translation entity (admin API) */
export interface IManualTranslationDto {
  id: string
  fieldName: string
  sourceText: string
  targetText: string
  language: string
  brand: { id: string; name: string }
  createdAt: string
  updatedAt: string
}

export interface IManualTranslationCreateItem {
  fieldName: string
  sourceText: string
  targetText: string
}

export interface IManualTranslationUpdateItem {
  id: string
  fieldName?: string
  sourceText?: string
  targetText?: string
  language?: string
}

export interface ICreateManualTranslationsRequest {
  translations: IManualTranslationCreateItem[]
  language: string
  brand: string
}

export interface IUpdateManualTranslationsRequest {
  translations: IManualTranslationUpdateItem[]
}

export interface IManualTranslationsListResponse {
  data: IManualTranslationDto[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages?: number
  }
}

export class TranslationRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  /** Public: get translations by brand and language (key-value map for frontend) */
  getTranslations({ brandId, country }: { brandId: string; country: string }) {
    return this.api.get(`/translations/${brandId}/${country}`, { skipAuth: true })
  }

  /** Admin: list manual translations (paginated). Uses limit[take] and limit[page] query params. */
  getManualTranslationsList(params?: { take?: number; page?: number }) {
    const limit = {
      take: params?.take ?? 500,
      page: params?.page ?? 1
    }
    const queryParams = {
      'limit[take]': limit.take,
      'limit[page]': limit.page
    }
    const queryString = qs.stringify(queryParams)

    return this.api.get(`/translations?${queryString}`)
  }

  /** Admin: create manual translations */
  createManualTranslations(payload: ICreateManualTranslationsRequest) {
    return this.api.post('/translations', payload)
  }

  /** Admin: update manual translations */
  updateManualTranslations(payload: IUpdateManualTranslationsRequest) {
    return this.api.patch('/translations', payload)
  }

  /** Admin: delete manual translations by ids */
  deleteManualTranslations(ids: string[]) {
    return this.api.delete('/translations', {
      params: { ids: JSON.stringify(ids) }
    })
  }

  getTranslationProgress(translationId: string) {
    return this.api.get(`/ai-translations/progress/${translationId}`)
  }

  cleanupTranslationProgress(translationId: string) {
    return this.api.delete(`/ai-translations/progress/${translationId}`)
  }
}

export default new TranslationRepository(HttpClient)
